import { useState, useEffect, useRef, memo, useCallback } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import { PlayArrow } from '@mui/icons-material'
import { Box, Fade } from '@mui/material'

import { checkIfAnimated } from '../../utils/image'

export type AnimatedImageProps = {
  /** URL of the image to display */
  url?: string
  /** Alt text for the image */
  alt?: string
  /** Additional CSS class names */
  className?: string
  /** Callback when image loads */
  onLoad?: () => void
  /** Callback when image fails to load */
  onError?: () => void
  /** Callback when image is clicked */
  onClick?: () => void
  /** Whether to show play icon on animatable images (default: true) */
  showPlayIcon?: boolean
  /** Custom play icon size (default: 48) */
  playIconSize?: number
  /** Whether to loop the animation (default: true) - reserved for future use */
  loop?: boolean
  /** Whether to automatically play animations or only on hover (default: false - hover only) */
  autoPlay?: boolean
  /** External control for playing animation (overrides internal hover state when provided) */
  isPlaying?: boolean
  /** Custom image style */
  imgStyle?: React.CSSProperties
}

/**
 * A component that displays images with support for animated formats (GIF, WebP).
 * For animated images, shows a play icon overlay and plays animation on hover.
 * The image will cover the entire container while maintaining aspect ratio.
 */
export const AnimatedImage: React.FC<AnimatedImageProps> = memo(
  ({ url, alt = '', className = '', onLoad, onError, onClick, showPlayIcon = true, playIconSize = 48, autoPlay = false, isPlaying, imgStyle }) => {
    const [isAnimatable, setIsAnimatable] = useState(false)
    const [isHovered, setIsHovered] = useState(false)
    const [imageLoaded, setImageLoaded] = useState(false)
    const [staticUrl, setStaticUrl] = useState<string | null>(null)
    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
      if (url) {
        // Reset state when URL changes
        setIsAnimatable(false)
        setImageLoaded(false)

        // Check if image is animatable
        checkIfAnimated(url).then(setIsAnimatable)
      }
    }, [url])

    // Extract first frame for animated images
    const extractFirstFrame = useCallback(() => {
      // Skip frame extraction in Firefox
      const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)
      if (isFirefox) return

      if (imageElement && canvasRef.current && isAnimatable) {
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        if (ctx) {
          //imageRef.current.setAttribute('crossorigin', 'anonymous')
          canvas.width = imageElement.naturalWidth
          canvas.height = imageElement.naturalHeight
          ctx.drawImage(imageElement, 0, 0)

          // Convert canvas to data URL for static display
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob)
              setStaticUrl(url)
            }
          }, 'image/png')
        }
      }
    }, [imageElement, isAnimatable])

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
      event.currentTarget.parentElement!.style.display = 'block'
      setImageElement(event.currentTarget)
      setImageLoaded(true)
      extractFirstFrame()
      onLoad?.()
    }

    // Re-extract first frame when animation state changes
    useEffect(() => {
      if (imageLoaded && isAnimatable) {
        extractFirstFrame()
      }
    }, [isAnimatable, imageLoaded, extractFirstFrame])

    // Clean up blob URL when component unmounts
    useEffect(() => {
      return () => {
        if (staticUrl) {
          URL.revokeObjectURL(staticUrl)
        }
      }
    }, [staticUrl])

    const handleMouseEnter = () => {
      setIsHovered(true)
    }

    const handleMouseLeave = () => {
      setIsHovered(false)
    }

    // Don't render anything if URL is falsy
    if (!url) {
      return null
    }

    // Determine if animation should be playing
    // If isPlaying is provided, use it; otherwise fall back to internal logic
    const shouldAnimate = isPlaying !== undefined ? isPlaying : autoPlay || isHovered

    return (
      <Box
        className={`AnimatedImage ${className}`}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Static image for non-animated or paused state */}
        {staticUrl && isAnimatable && !shouldAnimate && (
          <Box
            component="img"
            alt={alt}
            src={staticUrl}
            style={{
              ...imgStyle,
              display: 'block',
            }}
          />
        )}

        {/* Main animated image */}
        <LazyLoadImage
          alt={alt}
          src={url}
          effect="blur"
          onLoad={handleImageLoad}
          onError={onError}
          crossOrigin="anonymous"
          style={{
            ...imgStyle,
            display: !isAnimatable || shouldAnimate || !staticUrl ? 'block' : 'none',
          }}
        />

        {/* Hidden canvas for extracting first frame */}
        <Box
          component="canvas"
          ref={canvasRef}
          sx={{
            display: 'none',
          }}
        />

        {/* Play icon overlay */}
        <Fade in={isAnimatable && showPlayIcon && !shouldAnimate && imageLoaded} timeout={300}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: playIconSize,
                height: playIconSize,
                borderRadius: '50%',
                border: '2px dashed rgba(255, 255, 255, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(3px)',
              }}
            >
              <PlayArrow
                sx={{
                  fontSize: playIconSize,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              />
            </Box>
          </Box>
        </Fade>
      </Box>
    )
  }
)
